import { useEffect, useState, type FormEvent } from 'react';
import { BrandMark } from './BrandMark';
import { ApiError } from '../lib/api';
import type { AuthUser } from '../types';

interface AuthScreenProps {
  onSignedIn: (user: AuthUser) => void;
  signup: (email: string, password: string, acceptedTerms: boolean) => Promise<AuthUser>;
  login: (email: string, password: string) => Promise<AuthUser>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, password: string) => Promise<void>;
}

type Mode = 'login' | 'signup' | 'forgot' | 'reset';

// The app (app.z3ntra.ai) and the marketing site (z3ntra.ai) are separate
// deploys — see DEPLOYMENT.md — so a plain "/terms.html" link here would
// resolve against this app's own origin and 404. Mirrors how
// marketing/config.js hardcodes Z3NTRAFY_APP_URL for links the other
// direction: one line to edit if the marketing site's domain ever changes.
const MARKETING_URL = 'https://z3ntra.ai';

/**
 * The gate every session starts at: sign in if you have an account, sign up
 * if you don't, or recover a forgotten password. Kept as one small screen
 * with a mode toggle rather than separate routes, since there is nothing
 * else to navigate to before you're in.
 */
export function AuthScreen({ onSignedIn, signup, login, forgotPassword, resetPassword }: AuthScreenProps) {
  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Required to create an account, whether by password or Google — gates
  // both the submit button below and the Google button. Reset on every
  // mode switch rather than left checked, so leaving and returning to
  // "Create account" always starts unacknowledged.
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // The Google callback redirects back here with ?googleError=... on failure,
  // and the emailed reset link lands here with ?resetToken=... — both are
  // full page navigations, so they're read once on mount rather than caught
  // as a rejected promise, and scrubbed from the URL so a refresh doesn't
  // re-show a stale error or re-attempt a reset with an already-used token.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const googleError = params.get('googleError');
    const token = params.get('resetToken');
    if (googleError) {
      setError(googleError);
      window.history.replaceState({}, '', window.location.pathname);
    } else if (token) {
      setResetToken(token);
      setMode('reset');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  function switchMode(next: Mode) {
    setMode(next);
    setError(null);
    setNotice(null);
    setAcceptedTerms(false);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      if (mode === 'forgot') {
        await forgotPassword(email.trim());
        setNotice('If that email has an account, a reset link is on its way.');
        return;
      }
      if (mode === 'reset') {
        await resetPassword(resetToken, password);
        setNotice('Password updated. Sign in with your new password.');
        setMode('login');
        setPassword('');
        return;
      }
      const user =
        mode === 'signup' ? await signup(email.trim(), password, acceptedTerms) : await login(email.trim(), password);
      onSignedIn(user);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  }

  const isCredentialsMode = mode === 'login' || mode === 'signup';

  return (
    <div className="auth-screen">
      <div className="auth-card">
        <div className="brand" style={{ justifyContent: 'center', marginBottom: 18 }}>
          <BrandMark size={30} />
          <div className="brand-word">
            <span className="wordmark">Z3NTRAFY</span>
            <small>work across your systems</small>
          </div>
        </div>

        {isCredentialsMode && (
          <div className="auth-tabs" role="tablist">
            <button type="button" role="tab" aria-selected={mode === 'login'} className={mode === 'login' ? 'active' : ''} onClick={() => switchMode('login')}>
              Sign in
            </button>
            <button type="button" role="tab" aria-selected={mode === 'signup'} className={mode === 'signup' ? 'active' : ''} onClick={() => switchMode('signup')}>
              Create account
            </button>
          </div>
        )}

        {mode === 'signup' && (
          <label className="auth-consent">
            <input type="checkbox" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} />
            <span>
              I agree to Z3NTRAFY's{' '}
              <a href={`${MARKETING_URL}/terms.html`} target="_blank" rel="noreferrer">Terms &amp; Conditions</a>,{' '}
              <a href={`${MARKETING_URL}/privacy.html`} target="_blank" rel="noreferrer">Privacy Policy</a>, and{' '}
              <a href={`${MARKETING_URL}/acceptable-use.html`} target="_blank" rel="noreferrer">Acceptable Use Policy</a>.
            </span>
          </label>
        )}

        {isCredentialsMode && (
          <>
            {mode === 'signup' && !acceptedTerms ? (
              <button type="button" className="btn google-btn" disabled title="Accept the terms above first">
                <GoogleGlyph />
                Continue with Google
              </button>
            ) : (
              // On "Sign in", this button doubles as a first-time-signup entry
              // point (loginOrSignupWithGoogle creates the account if it's
              // brand new) — there's no checkbox on this tab to gate on, and
              // there's no email/password yet at click time to know in
              // advance whether it'll be a new account. `terms=1` is sent
              // unconditionally here, with consent covered instead by the
              // disclosure line below the button, so a first-time Google
              // sign-in from this tab never dead-ends on the "please check
              // the box" error — there's no box on this tab to check.
              <a className="btn google-btn" href="/api/auth/google?terms=1">
                <GoogleGlyph />
                Continue with Google
              </a>
            )}
            {mode === 'login' && (
              <p className="auth-disclosure">
                If this creates a new account, you agree to Z3NTRAFY's{' '}
                <a href={`${MARKETING_URL}/terms.html`} target="_blank" rel="noreferrer">Terms &amp; Conditions</a>,{' '}
                <a href={`${MARKETING_URL}/privacy.html`} target="_blank" rel="noreferrer">Privacy Policy</a>, and{' '}
                <a href={`${MARKETING_URL}/acceptable-use.html`} target="_blank" rel="noreferrer">Acceptable Use Policy</a>.
              </p>
            )}
            <div className="auth-divider">
              <span>or</span>
            </div>
          </>
        )}

        {mode === 'forgot' && <p className="hint">Enter your account email and we'll send a link to reset your password.</p>}
        {mode === 'reset' && <p className="hint">Choose a new password for your account.</p>}

        <form onSubmit={onSubmit} className="auth-form">
          {(mode === 'login' || mode === 'signup' || mode === 'forgot') && (
            <label>
              Email
              <input
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
              />
            </label>
          )}
          {(mode === 'login' || mode === 'signup' || mode === 'reset') && (
            <label>
              {mode === 'reset' ? 'New password' : 'Password'}
              <input
                type="password"
                autoComplete={mode === 'signup' || mode === 'reset' ? 'new-password' : 'current-password'}
                required
                minLength={mode === 'signup' || mode === 'reset' ? 8 : undefined}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === 'signup' || mode === 'reset' ? 'At least 8 characters' : undefined}
              />
            </label>
          )}

          {error && <div className="auth-error">{error}</div>}
          {notice && <div className="auth-notice">{notice}</div>}

          <button className="btn primary" type="submit" disabled={busy || (mode === 'signup' && !acceptedTerms)}>
            {busy ? <span className="spin" /> : null}
            {mode === 'signup' ? 'Create account' : mode === 'forgot' ? 'Send reset link' : mode === 'reset' ? 'Set new password' : 'Sign in'}
          </button>
        </form>

        {mode === 'login' && (
          <p className="auth-switch">
            <button type="button" className="link" onClick={() => switchMode('forgot')}>
              Forgot password?
            </button>
          </p>
        )}

        {isCredentialsMode && (
          <p className="auth-switch">
            {mode === 'signup' ? 'Already have an account?' : "Don't have an account?"}{' '}
            <button type="button" className="link" onClick={() => switchMode(mode === 'signup' ? 'login' : 'signup')}>
              {mode === 'signup' ? 'Sign in' : 'Create one'}
            </button>
          </p>
        )}

        {(mode === 'forgot' || mode === 'reset') && (
          <p className="auth-switch">
            <button type="button" className="link" onClick={() => switchMode('login')}>
              Back to sign in
            </button>
          </p>
        )}
      </div>
    </div>
  );
}

/** Google's standard multi-color "G" glyph, inlined so the button needs no external asset. */
function GoogleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.71v2.26h2.9c1.7-1.57 2.7-3.87 2.7-6.61Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.19l-2.9-2.26c-.8.54-1.84.86-3.06.86-2.35 0-4.34-1.59-5.05-3.72H.94v2.33A9 9 0 0 0 9 18Z"
      />
      <path fill="#FBBC05" d="M3.95 10.69A5.4 5.4 0 0 1 3.67 9c0-.59.1-1.16.28-1.69V4.98H.94A9 9 0 0 0 0 9c0 1.45.35 2.83.94 4.02l3.01-2.33Z" />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.51.46 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .94 4.98l3.01 2.33C4.66 5.17 6.65 3.58 9 3.58Z"
      />
    </svg>
  );
}
